// Le monde : une carte carrée centrée sur l'origine, peuplée de fantômes dispersés qui dérivent.
// Bouclée en X (voir core/camera.js#wrapX) : les distances/écarts en X passent par le chemin le
// plus court, direct ou par la couture. Fonctions pures, sans DOM ; le générateur aléatoire est
// injectable pour des tests déterministes.
import { WORLD_MARGIN, GHOST_EDGE_MARGIN, CENTER_CLEARANCE, GHOST_MAX_SIZE, GHOST_EDGE_TURN_BAND, VIEW_SIZE } from '../config/constants.js';
import { familyMember, FAMILY_MEMBERS, UNLOCK_ORDER } from '../data/family.js';
import { wrapX, clamp, isInView } from './camera.js';

const MAX_ATTEMPTS_PER_GHOST = 200;

/**
 * Place `count` fantômes dans un monde de côté `worldSize`, à au moins `minSpacing` l'un de
 * l'autre et jamais dans le viseur au départ (centre). Si la contrainte d'espacement n'est
 * pas satisfiable, elle est relâchée progressivement plutôt que de boucler sans fin.
 * @param {object} options
 * @param {number} options.count
 * @param {number} options.worldSize
 * @param {number} options.minSpacing
 * @param {string[]} options.colors  attribuées en boucle
 * @param {() => number} [options.random]
 * @returns {Ghost[]}
 * @typedef {{ id:number, x:number, y:number, heading:number, pace:number, wander:number,
 *   color:string, familyIndex:number, captured:boolean, known:boolean }} Ghost
 *   heading : cap en radians ; pace/wander : caractère du membre (voir data/family.js)
 */
export function generateGhosts({ count, worldSize, minSpacing, colors, familySize = FAMILY_MEMBERS.length, order = UNLOCK_ORDER, known = new Set(), random = Math.random }) {
  const halfX = worldSize / 2 - WORLD_MARGIN;
  const halfY = Math.max(0, worldSize / 2 - GHOST_EDGE_MARGIN); // en Y, jamais plus près du bord que ne le vont les kamis
  const ghosts = [];
  let spacing = minSpacing;

  for (let i = 0; i < count; i++) {
    let placed = null;
    while (!placed) {
      for (let attempt = 0; attempt < MAX_ATTEMPTS_PER_GHOST && !placed; attempt++) {
        const x = (random() * 2 - 1) * halfX;
        const y = (random() * 2 - 1) * halfY;
        if (Math.hypot(x, y) < CENTER_CLEARANCE) continue;
        if (ghosts.some((g) => Math.hypot(g.x - x, g.y - y) < spacing)) continue;
        placed = { x, y };
      }
      if (!placed) spacing *= 0.8; // monde trop petit pour l'espacement demandé : on relâche
    }
    // Les `familySize` premiers membres de l'ordre d'arrivée (Ujin en dernier), répétés au fil des niveaux ; l'indice reste
    // unique (type = indice % 15)
    const familyIndex = order[i % familySize] + FAMILY_MEMBERS.length * Math.floor(i / familySize);
    const member = familyMember(familyIndex);
    ghosts.push({
      id: i,
      x: placed.x,
      y: placed.y,
      heading: random() * Math.PI * 2,
      pace: member.pace,
      wander: member.wander,
      color: colors[i % colors.length],
      familyIndex,
      captured: false,
      // Kami déjà photographié aux niveaux précédents : présent sur la carte (il rend la traque plus difficile) mais ce
      // n'est plus une cible : ni cadrage, ni compte, ni aide, ni approche
      known: known.has(familyIndex % FAMILY_MEMBERS.length),
    });
  }
  return ghosts;
}

/** Le fantôme retenu par `accept` le plus proche du centre du viseur, dans le rayon de capture ; null sinon. */
function closestCentered(ghosts, camera, captureRadius, worldSize, accept) {
  let best = null;
  let bestDistance = Infinity;
  for (const g of ghosts) {
    if (!accept(g)) continue;
    const dx = wrapX(g.x - camera.x, worldSize);
    const d = Math.hypot(dx, g.y - camera.y);
    if (d <= captureRadius && d < bestDistance) {
      best = g;
      bestDistance = d;
    }
  }
  return best;
}

/**
 * Le fantôme (non capturé) le plus proche du centre du viseur, s'il est dans le rayon de capture.
 * @param {number} [worldSize]  pour l'écart en X par la couture (voir wrapX) ; sans lui, écart brut
 * @returns {Ghost|null}
 */
export function findCenteredGhost(ghosts, camera, captureRadius, worldSize) {
  return closestCentered(ghosts, camera, captureRadius, worldSize, (g) => !g.captured && !g.known);
}

/**
 * Le kami déjà dans l'album (`known` : photographié à un niveau passé, ou avant une batterie vide) le plus proche du centre du
 * viseur, s'il est dans le rayon de capture. Il reste sur la carte mais ne se photographie plus : le jeu le dit au lieu de
 * gâcher une photo (#20).
 * @param {number} [worldSize]  pour l'écart en X par la couture (voir wrapX) ; sans lui, écart brut
 * @returns {Ghost|null}
 */
export function findCenteredKnownGhost(ghosts, camera, captureRadius, worldSize) {
  return closestCentered(ghosts, camera, captureRadius, worldSize, (g) => !g.captured && g.known);
}

/**
 * Bornes verticales de la dérive d'un fantôme : un peu en deçà de ce que l'objectif peut atteindre en butée (getPanLimit) —
 * GHOST_EDGE_MARGIN du bord : demi-hauteur du viseur plus un cercle du réticule —, pour qu'on puisse toujours le mettre au
 * centre du viseur et qu'il vire avant la butée. Le Y seulement : l'axe X boucle, sans bord.
 * @returns {{ top:number, bottom:number }}
 */
export function ghostYLimits(worldSize) {
  const limit = Math.max(0, worldSize / 2 - GHOST_EDGE_MARGIN);
  return { top: -limit, bottom: limit };
}

// Une fois le virage entamé près d'un bord, on continue de tourner jusqu'à repartir au moins de
// cet angle vers l'intérieur (et pas seulement parallèle au bord, où il longerait le décor).
const EDGE_DEPARTURE_SIN = Math.sin(Math.PI / 9); // 20°

/**
 * Près du ciel ou du sol, le cap vers lequel virer (la verticale vers l'intérieur), ou null si le
 * fantôme n'est pas dans la bande de virage ou s'en éloigne déjà franchement.
 */
function edgeTurnTarget(g, limits, band) {
  const towardBottom = Math.sin(g.heading);
  if (g.y > limits.bottom - band && towardBottom > -EDGE_DEPARTURE_SIN) return -Math.PI / 2;
  if (g.y < limits.top + band && towardBottom < EDGE_DEPARTURE_SIN) return Math.PI / 2;
  return null;
}

/**
 * Fait dériver les fantômes : chacun avance à `speed × pace` et infléchit son cap au hasard selon
 * son `wander`. En X, il boucle avec le monde (wrapX). En Y, il va jusqu'au bord du décor : dans
 * la bande GHOST_EDGE_TURN_BAND, s'il s'en approche, son cap vire progressivement vers
 * l'intérieur (rayon de virage = largeur de la bande) — une courbe en U, jamais un rebond. Ne
 * fait rien si speed <= 0. Mutation en place.
 * @param {Ghost[]} ghosts
 * @param {number} dtSeconds  temps écoulé depuis la dernière frame
 * @param {number} speed      vitesse de référence, unités monde par seconde
 * @param {number} worldSize
 * @param {() => number} [random]
 * @param {number|null} [skipId]  fantôme laissé de côté (ex. en cours d'approche dirigée, voir stepGhostToward)
 * @param {{x:number,y:number}|null} [camera]  si fournie, un fantôme visible dans le viseur (portrait compris) ne
 *   change jamais de sens : il file droit et ne fait ses détours qu'une fois hors de l'écran. Seul le virage
 *   de bord (le décor s'arrête là) reste visible, il est impossible à éviter.
 */
export function moveGhosts(ghosts, dtSeconds, speed, worldSize, random = Math.random, skipId = null, camera = null) {
  if (!(speed > 0) || !(dtSeconds > 0)) return;
  const limits = ghostYLimits(worldSize);
  const band = GHOST_EDGE_TURN_BAND;
  const dt = Math.min(dtSeconds, 0.1); // onglet en arrière-plan : pas de saut géant au retour
  for (const g of ghosts) {
    if (g.captured || g.id === skipId) continue;
    const v = speed * (g.pace ?? 1);
    const target = edgeTurnTarget(g, limits, band);
    if (target !== null) {
      // Virage à vitesse angulaire v / band, par le plus court : le fantôme garde son sens de
      // marche horizontal et décrit un U, au lieu du « miroir » instantané d'un rebond.
      const diff = angleDiff(g.heading, target);
      const maxTurn = (v / band) * dt;
      g.heading += clamp(diff, -maxTurn, maxTurn);
    } else if (!(camera && isInView(g, camera, GHOST_MAX_SIZE / 2, VIEW_SIZE, worldSize))) {
      // Cap infléchi au hasard (marche aléatoire lissée) : wander = radians/s d'amplitude max. Jamais à
      // l'écran : on ne voit pas un kami hésiter, il change de cap hors de vue.
      g.heading += (random() * 2 - 1) * (g.wander ?? 0.5) * dt * 2;
    }
    g.x = wrapX(g.x + Math.cos(g.heading) * v * dt, worldSize);
    // Filet de sécurité (virage entamé trop tard, ex. fantôme relâché par l'approche dirigée) :
    // il glisse le long du bord le temps de finir son virage, sans rebondir.
    g.y = clamp(g.y + Math.sin(g.heading) * v * dt, limits.top, limits.bottom);
  }
}

/**
 * Le fantôme non capturé le plus proche d'un point donné (sans notion de rayon : sert à choisir
 * qui approcher, voir stepGhostToward, contrairement à findCenteredGhost qui vérifie le cadrage).
 * @param {number} [worldSize]  pour l'écart en X par la couture (voir wrapX) ; sans lui, écart brut
 * @returns {Ghost|null}
 */
export function nearestGhost(ghosts, point, worldSize) {
  let best = null;
  let bestDistance = Infinity;
  for (const g of ghosts) {
    if (g.captured || g.known) continue;
    const dx = wrapX(g.x - point.x, worldSize);
    const d = Math.hypot(dx, g.y - point.y);
    if (d < bestDistance) {
      best = g;
      bestDistance = d;
    }
  }
  return best;
}

/**
 * Aide « approche », phase 1 : après une longue inactivité, un fantôme vient vers `target` (en
 * pratique la caméra) en ligne droite, à vitesse fixe. Mutation en place. Retourne true dès qu'il
 * est entré dans `vicinity` : c'est le signal pour passer en phase 2 (voir wanderNear), pas un
 * arrêt — le fantôme ne doit jamais rester figé.
 * @param {Ghost} ghost
 * @param {{x:number,y:number}} target
 * @param {number} speed     unités monde par seconde, indépendant de la dérive du niveau
 * @param {number} vicinity
 * @param {number} dtSeconds
 * @param {number} [worldSize]  pour approcher par la couture si c'est le chemin le plus court (wrapX)
 */
export function stepGhostToward(ghost, target, speed, vicinity, dtSeconds, worldSize) {
  const dx = wrapX(target.x - ghost.x, worldSize);
  const dy = target.y - ghost.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= vicinity) return true;
  const dt = Math.min(dtSeconds, 0.1);
  const step = Math.min(dist, speed * dt);
  ghost.x = wrapX(ghost.x + (dx / dist) * step, worldSize);
  ghost.y += (dy / dist) * step;
  ghost.heading = Math.atan2(dy, dx); // s'il redérive ensuite, il repart dans cette direction
  return false;
}

/** Différence angulaire la plus courte de `from` vers `to`, dans [-π, π]. */
function angleDiff(from, to) {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

/**
 * Aide « approche », phase 2 : une fois arrivé (voir stepGhostToward), le fantôme continue de
 * flâner au lieu de rester figé sous le viseur. Il file en ligne droite, sans jamais changer de sens
 * tant qu'il est à l'écran ; au-delà de `radius` autour de `center` (la caméra), que l'appelant fixe
 * hors de l'écran, le cap est ramené vers le centre sur une courte distance : le fantôme sort un peu,
 * puis revient en courbe, jamais un demi-tour brutal contre un mur invisible. Mutation en place.
 * @param {Ghost} ghost
 * @param {{x:number,y:number}} center
 * @param {number} radius
 * @param {number} speed
 * @param {number} dtSeconds
 * @param {() => number} [random]
 * @param {number} [worldSize]  pour mesurer/rappeler vers le centre par la couture (wrapX)
 */
export function wanderNear(ghost, center, radius, speed, dtSeconds, random = Math.random, worldSize) {
  const dt = Math.min(dtSeconds, 0.1);
  const dx = wrapX(center.x - ghost.x, worldSize);
  const dy = center.y - ghost.y;
  const dist = Math.hypot(dx, dy);
  if (dist > radius) {
    // Plus on s'éloigne au-delà du rayon, plus le cap est rappelé vers le centre (rappel complet
    // 40 unités plus loin) : une courbe de retour, hors de vue, pas un rebond.
    const toCenter = Math.atan2(dy, dx);
    const pull = Math.min(1, (dist - radius) / Math.min(radius * 0.5, 40));
    ghost.heading += angleDiff(ghost.heading, toCenter) * pull;
  }
  const v = speed * (ghost.pace ?? 1);
  ghost.x = wrapX(ghost.x + Math.cos(ghost.heading) * v * dt, worldSize);
  ghost.y += Math.sin(ghost.heading) * v * dt;
  if (worldSize > 0) {
    // Caméra en butée : la courbe de retour peut frôler le bord du décor, jamais le dépasser
    const { top, bottom } = ghostYLimits(worldSize);
    ghost.y = clamp(ghost.y, top, bottom);
  }
}

/**
 * Types de kami (familyIndex % FAMILY_MEMBERS.length) déjà photographiés au moins une fois.
 * Au-delà du niveau 1, plusieurs fantômes partagent le même type (voir core/level.js) : la cible
 * du niveau est ce compte, jamais le nombre brut de fantômes capturés (qui grossirait pour rien
 * avec les doublons).
 */
export function countFoundTypes(ghosts) {
  const found = new Set();
  for (const g of ghosts) if (g.captured || g.known) found.add(g.familyIndex % FAMILY_MEMBERS.length);
  return found.size;
}

/** Types de kami pas encore photographiés une seule fois (0..familySize). */
export function countRemainingTypes(ghosts, familySize = FAMILY_MEMBERS.length) {
  return familySize - countFoundTypes(ghosts);
}
